import { useEffect, useMemo, useState } from 'react'
import { Loader2, Radio } from 'lucide-react'
import { cn } from '../atoms'

export interface LiveIndicatorProps {
  lastUpdatedAt: number
  isRefreshing: boolean
  className?: string
  label?: string
}

export function LiveIndicator({ lastUpdatedAt, isRefreshing, className, label = 'En vivo' }: LiveIndicatorProps) {
  const [, setTick] = useState(0)

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1_000)
    return () => clearInterval(id)
  }, [])

  const relative = useMemo(() => {
    if (!lastUpdatedAt) return '—'
    const diffMs = Date.now() - lastUpdatedAt
    if (diffMs < 5_000) return 'ahora'
    if (diffMs < 60_000) return `hace ${Math.floor(diffMs / 1_000)}s`
    if (diffMs < 3_600_000) return `hace ${Math.floor(diffMs / 60_000)}min`
    return new Date(lastUpdatedAt).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })
  }, [lastUpdatedAt])

  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-medium text-emerald-700',
        className,
      )}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {isRefreshing ? (
        <>
          <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
          <span>Sincronizando…</span>
          <span className="sr-only">Actualizando datos</span>
        </>
      ) : (
        <>
          <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
          <Radio className="w-3.5 h-3.5" aria-hidden="true" />
          <span>{label} · Actualizado {relative}</span>
          <span className="sr-only">Datos actualizándose automáticamente</span>
        </>
      )}
    </div>
  )
}
